/**
 * Consultas GraphQL contra Suwayomi.
 *
 * Los nombres de campo estan verificados por introspeccion del esquema de
 * la v2.3.2243; no son suposiciones.
 */

import { aMilisegundos } from '../lib/fechas'
import { gql } from './cliente'

export interface Fuente {
  id: string
  name: string
  lang: string
  isNsfw: boolean
  supportsLatest: boolean
}

export interface EntradaMeta {
  key: string
  value: string
}

export interface Manga {
  id: number
  title: string
  thumbnailUrl: string | null
  inLibrary: boolean
  unreadCount: number
  downloadCount: number
  source: { id: string; name: string; lang: string } | null
  /**
   * Metadatos propios guardados en Suwayomi. Es donde vive la marca de
   * "finalizada" (ver `lib/finalizadas`): texto suelto por clave, que viaja
   * a los demas aparatos y **entra en el .tachibk de las copias diarias**
   * —comprobado exportando una copia con una clave de prueba dentro—.
   */
  meta: EntradaMeta[]
}

export interface MangaDetalle extends Manga {
  author: string | null
  artist: string | null
  description: string | null
  genre: string[]
  status: string
  realUrl: string | null
}

export interface Capitulo {
  id: number
  name: string
  chapterNumber: number
  scanlator: string | null
  isRead: boolean
  isDownloaded: boolean
  isBookmarked: boolean
  lastPageRead: number
  pageCount: number
  uploadDate: string
  sourceOrder: number
  /** Cuando se leyo por ultima vez, en segundos. Llega como texto. */
  lastReadAt: string | null
}

export interface Categoria {
  id: number
  name: string
  order: number
  default: boolean
}

const CAMPOS_MANGA = `
  id
  title
  thumbnailUrl
  inLibrary
  unreadCount
  downloadCount
  source { id name lang }
  meta { key value }
`

/** Lo que hace falta para ordenar la biblioteca, y solo se pide alli. */
interface MangaBiblioteca extends Manga {
  /** Cuando se alta en la biblioteca, en segundos y como texto */
  inLibraryAt: string | null
  /**
   * El capitulo que se leyo mas recientemente, no el mas avanzado. Sin
   * empezar la serie llega con `lastReadAt` a cero, que no es lo mismo que
   * llegar a nulo: hay que tratar los dos como "sin leer".
   */
  lastReadChapter: { lastReadAt: string | null } | null
}

/** Segundos de la ultima lectura, o 0 si la serie no se ha empezado. */
function ultimaLectura(manga: MangaBiblioteca): number {
  return Number(manga.lastReadChapter?.lastReadAt ?? 0) || 0
}

/**
 * La biblioteca, con lo que estas leyendo delante.
 *
 * Suwayomi las devuelve en el orden en que se dieron de alta, que envejece
 * mal: la serie del momento se hunde y la que se abandono hace dos meses
 * sigue arriba. Se ordena por cuando se leyo cada una, que es lo que hace
 * que abrir la app y seguir por donde ibas sea un toque.
 *
 * Detras van las que ni se han empezado, y esas por fecha de alta al reves:
 * lo que acabas de anadir es lo que mas posibilidades tiene de que quieras
 * empezarlo.
 *
 * Se ordena aqui y no en la consulta porque el `order` de Suwayomi no llega
 * a `lastReadChapter`, que es un campo calculado.
 */
export async function obtenerBiblioteca(): Promise<Manga[]> {
  const datos = await gql<{ mangas: { nodes: MangaBiblioteca[] } }>(`
    query {
      mangas(condition: { inLibrary: true }) {
        nodes {
          ${CAMPOS_MANGA}
          inLibraryAt
          lastReadChapter { lastReadAt }
        }
      }
    }
  `)
  return [...datos.mangas.nodes].sort((a, b) => {
    const leidaA = ultimaLectura(a)
    const leidaB = ultimaLectura(b)
    if (leidaA !== leidaB) return leidaB - leidaA
    return Number(b.inLibraryAt ?? 0) - Number(a.inLibraryAt ?? 0)
  })
}

export async function obtenerCategorias(): Promise<Categoria[]> {
  const datos = await gql<{ categories: { nodes: Categoria[] } }>(`
    query {
      categories {
        nodes { id name order default }
      }
    }
  `)
  return [...datos.categories.nodes].sort((a, b) => a.order - b.order)
}

export async function obtenerFuentes(): Promise<Fuente[]> {
  const datos = await gql<{ sources: { nodes: Fuente[] } }>(`
    query {
      sources {
        nodes { id name lang isNsfw supportsLatest }
      }
    }
  `)
  return datos.sources.nodes
}

export async function obtenerManga(id: number): Promise<MangaDetalle> {
  const datos = await gql<{ manga: MangaDetalle }>(
    `
    query ($id: Int!) {
      manga(id: $id) {
        ${CAMPOS_MANGA}
        author
        artist
        description
        genre
        status
        realUrl
      }
    }
  `,
    { id },
  )
  return datos.manga
}

export async function obtenerCapitulos(mangaId: number): Promise<Capitulo[]> {
  const datos = await gql<{ chapters: { nodes: Capitulo[] } }>(
    `
    query ($mangaId: Int!) {
      chapters(condition: { mangaId: $mangaId }) {
        nodes {
          id name chapterNumber scanlator isRead isDownloaded
          isBookmarked lastPageRead pageCount uploadDate sourceOrder lastReadAt
        }
      }
    }
  `,
    { mangaId },
  )
  return [...datos.chapters.nodes].sort((a, b) => a.sourceOrder - b.sourceOrder)
}

export interface SeriePendiente {
  id: number
  title: string
  thumbnailUrl: string | null
  unreadCount: number
  lastReadChapter: { id: number; lastReadAt: string | null } | null
  meta: EntradaMeta[]
}

/**
 * La biblioteca con lo que hace falta para saber que tocaria leer.
 *
 * `lastReadChapter` es el capitulo leido mas recientemente, no el mas
 * avanzado: sirve para ordenar las series por cual estas siguiendo de
 * verdad, que no es lo mismo que cual tiene mas pendientes.
 */
export async function obtenerSeriesPendientes(): Promise<SeriePendiente[]> {
  const datos = await gql<{ mangas: { nodes: SeriePendiente[] } }>(`
    query {
      mangas(condition: { inLibrary: true }) {
        nodes {
          id
          title
          thumbnailUrl
          unreadCount
          lastReadChapter { id lastReadAt }
          meta { key value }
        }
      }
    }
  `)
  return datos.mangas.nodes
}

/** Los capitulos sin leer de una serie, del mas antiguo al mas nuevo. */
export async function obtenerNoLeidos(mangaId: number): Promise<Capitulo[]> {
  const datos = await gql<{ chapters: { nodes: Capitulo[] } }>(
    `
    query ($mangaId: Int!) {
      chapters(condition: { mangaId: $mangaId, isRead: false }) {
        nodes {
          id name chapterNumber scanlator isRead isDownloaded
          isBookmarked lastPageRead pageCount uploadDate sourceOrder lastReadAt
        }
      }
    }
  `,
    { mangaId },
  )
  return [...datos.chapters.nodes].sort((a, b) => a.sourceOrder - b.sourceOrder)
}

export type TipoListado = 'POPULAR' | 'LATEST' | 'SEARCH'

/**
 * Pide un listado a una fuente. Es una mutacion y no una consulta porque
 * Suwayomi va a la web externa y guarda el resultado en su base de datos.
 */
export async function explorarFuente(
  fuenteId: string,
  tipo: TipoListado,
  pagina = 1,
  busqueda?: string,
): Promise<{ mangas: Manga[]; hayMas: boolean }> {
  const datos = await gql<{
    fetchSourceManga: { hasNextPage: boolean; mangas: Manga[] }
  }>(
    `
    mutation ($fuente: LongString!, $tipo: FetchSourceMangaType!, $pagina: Int!, $busqueda: String) {
      fetchSourceManga(input: { source: $fuente, type: $tipo, page: $pagina, query: $busqueda }) {
        hasNextPage
        mangas { ${CAMPOS_MANGA} }
      }
    }
  `,
    { fuente: fuenteId, tipo, pagina, busqueda },
  )
  return {
    mangas: datos.fetchSourceManga.mangas,
    hayMas: datos.fetchSourceManga.hasNextPage,
  }
}

/** Refresca la lista de capitulos desde la web de la fuente. */
export async function refrescarCapitulos(mangaId: number): Promise<Capitulo[]> {
  const datos = await gql<{ fetchChapters: { chapters: Capitulo[] } }>(
    `
    mutation ($mangaId: Int!) {
      fetchChapters(input: { mangaId: $mangaId }) {
        chapters {
          id name chapterNumber scanlator isRead isDownloaded
          isBookmarked lastPageRead pageCount uploadDate sourceOrder lastReadAt
        }
      }
    }
  `,
    { mangaId },
  )
  return datos.fetchChapters.chapters
}

export async function cambiarEnBiblioteca(mangaId: number, dentro: boolean): Promise<void> {
  await gql(
    `
    mutation ($id: Int!, $dentro: Boolean!) {
      updateManga(input: { id: $id, patch: { inLibrary: $dentro } }) {
        manga { id inLibrary }
      }
    }
  `,
    { id: mangaId, dentro },
  )
}

/**
 * Guarda un metadato propio de una serie en el servidor.
 *
 * Suwayomi solo acepta texto por clave, asi que lo que tenga estructura va
 * como JSON. A cambio se gana lo que no da guardarlo en el aparato: se ve
 * igual en el iPhone y en el PC, y lo recoge la copia diaria.
 */
export async function guardarMeta(mangaId: number, clave: string, valor: string): Promise<void> {
  await gql(
    `
    mutation ($mangaId: Int!, $clave: String!, $valor: String!) {
      setMangaMeta(input: { meta: { mangaId: $mangaId, key: $clave, value: $valor } }) {
        meta { key value }
      }
    }
  `,
    { mangaId, clave, valor },
  )
}

/** Borrar una clave que no existe no es un error: devuelve `meta` a nulo. */
export async function borrarMeta(mangaId: number, clave: string): Promise<void> {
  await gql(
    `
    mutation ($mangaId: Int!, $clave: String!) {
      deleteMangaMeta(input: { mangaId: $mangaId, key: $clave }) {
        meta { key }
      }
    }
  `,
    { mangaId, clave },
  )
}

export interface CapituloConManga extends Capitulo {
  mangaId: number
  manga: { id: number; title: string; thumbnailUrl: string | null }
}

export async function obtenerCapitulo(id: number): Promise<CapituloConManga> {
  const datos = await gql<{ chapter: CapituloConManga }>(
    `
    query ($id: Int!) {
      chapter(id: $id) {
        id name chapterNumber scanlator isRead isDownloaded isBookmarked
        lastPageRead pageCount uploadDate sourceOrder mangaId
        manga { id title thumbnailUrl }
      }
    }
  `,
    { id },
  )
  return datos.chapter
}

/**
 * Pide las paginas de un capitulo.
 *
 * Es una mutacion porque Suwayomi va a la fuente externa a resolverlas.
 * Devuelve rutas relativas del propio servidor, no de la web original.
 */
export async function obtenerPaginas(capituloId: number): Promise<string[]> {
  const datos = await gql<{ fetchChapterPages: { pages: string[] } }>(
    `
    mutation ($id: Int!) {
      fetchChapterPages(input: { chapterId: $id }) {
        pages
      }
    }
  `,
    { id: capituloId },
  )
  return datos.fetchChapterPages.pages
}

/** Guarda por donde va la lectura. El servidor es la fuente de verdad. */
export async function guardarProgreso(
  capituloId: number,
  ultimaPagina: number,
  leido?: boolean,
): Promise<void> {
  await gql(
    `
    mutation ($id: Int!, $pagina: Int!, $leido: Boolean) {
      updateChapter(input: { id: $id, patch: { lastPageRead: $pagina, isRead: $leido } }) {
        chapter { id lastPageRead isRead }
      }
    }
  `,
    { id: capituloId, pagina: ultimaPagina, leido },
  )
}

export async function marcarLeido(capituloIds: number[], leido: boolean): Promise<void> {
  await gql(
    `
    mutation ($ids: [Int!]!, $leido: Boolean!) {
      updateChapters(input: { ids: $ids, patch: { isRead: $leido } }) {
        chapters { id isRead }
      }
    }
  `,
    { ids: capituloIds, leido },
  )
}

/**
 * Encola capitulos para descargar en el servidor (quedan en el VPS).
 *
 * Hay que arrancar el descargador a mano: Suwayomi lo deja en STOPPED y
 * encolar no lo despierta, asi que los capitulos se quedaban en la cola sin
 * que nadie los procesara. Al vaciarse la cola vuelve a pararse solo.
 */
export async function descargarEnServidor(capituloIds: number[]): Promise<void> {
  await gql(
    `
    mutation ($ids: [Int!]!) {
      enqueueChapterDownloads(input: { ids: $ids }) {
        clientMutationId
      }
    }
  `,
    { ids: capituloIds },
  )

  await gql(`mutation { startDownloader(input: {}) { downloadStatus { state } } }`)
}

export interface EnCola {
  capituloId: number
  nombre: string
  progreso: number
  estado: string
}

export async function estadoDescargas(): Promise<{ estado: string; cola: EnCola[] }> {
  const datos = await gql<{
    downloadStatus: {
      state: string
      queue: { chapter: { id: number; name: string }; progress: number; state: string }[]
    }
  }>(`
    query {
      downloadStatus {
        state
        queue { chapter { id name } progress state }
      }
    }
  `)
  return {
    estado: datos.downloadStatus.state,
    cola: datos.downloadStatus.queue.map((e) => ({
      capituloId: e.chapter.id,
      nombre: e.chapter.name,
      progreso: e.progress,
      estado: e.state,
    })),
  }
}

/** Borra del servidor los capitulos ya descargados. */
export async function borrarDescargas(capituloIds: number[]): Promise<void> {
  await gql(
    `
    mutation ($ids: [Int!]!) {
      deleteDownloadedChapters(input: { ids: $ids }) {
        chapters { id isDownloaded }
      }
    }
  `,
    { ids: capituloIds },
  )
}

export interface EstadoActualizacion {
  enMarcha: boolean
  total: number
  hechos: number
}

/**
 * Lanza la comprobacion de capitulos nuevos en toda la biblioteca.
 *
 * El servidor tambien la hace solo cada 6 horas; esto es para forzarla.
 * Devuelve en cuanto encola el trabajo, no cuando termina: el progreso se
 * sigue con `estadoActualizacion`.
 */
export async function actualizarBiblioteca(): Promise<void> {
  await gql(`
    mutation {
      updateLibrary(input: {}) {
        updateStatus { jobsInfo { isRunning totalJobs finishedJobs } }
      }
    }
  `)
}

export async function estadoActualizacion(): Promise<EstadoActualizacion> {
  const datos = await gql<{
    libraryUpdateStatus: {
      jobsInfo: { isRunning: boolean; totalJobs: number; finishedJobs: number }
    }
  }>(`
    query {
      libraryUpdateStatus { jobsInfo { isRunning totalJobs finishedJobs } }
    }
  `)
  const info = datos.libraryUpdateStatus.jobsInfo
  return { enMarcha: info.isRunning, total: info.totalJobs, hechos: info.finishedJobs }
}

/** Milisegundos de la ultima actualizacion, o 0 si nunca se ha hecho. */
export async function ultimaActualizacion(): Promise<number> {
  const datos = await gql<{ lastUpdateTimestamp: { timestamp: string } }>(`
    query { lastUpdateTimestamp { timestamp } }
  `)
  return Number(datos.lastUpdateTimestamp.timestamp) || 0
}

/** Comprueba que el servidor responde y las credenciales valen. */
export async function comprobarConexion(): Promise<{ version: string; revision: string }> {
  const datos = await gql<{ aboutServer: { version: string; revision: string } }>(`
    query { aboutServer { version revision } }
  `)
  return datos.aboutServer
}

export interface Novedad {
  id: number
  name: string
  chapterNumber: number
  scanlator: string | null
  isRead: boolean
  uploadDate: string
  manga: { id: number; title: string; thumbnailUrl: string | null }
}

const CAMPOS_NOVEDAD = `
  nodes {
    id name chapterNumber scanlator isRead uploadDate
    manga { id title thumbnailUrl }
  }
`

/*
 * El limite que separa las dos unidades de fecha (ver lib/fechas.ts). Aqui
 * hace falta ademas para pedirlas: ordenar por uploadDate en el servidor
 * mezcla peras con manzanas, y las fuentes que mandan segundos —numeros mil
 * veces mas pequenos— caerian siempre al final de la lista. One Piece no
 * aparecia en las novedades por esto. Se piden las dos escalas por separado,
 * en la misma peticion, y se ordenan juntas ya normalizadas.
 */
const LIMITE_UNIDADES = '100000000000'

/** Ultimos capitulos publicados en las series de la biblioteca. */
export async function obtenerNovedades(cuantas = 40): Promise<Novedad[]> {
  const datos = await gql<{
    enMilisegundos: { nodes: Novedad[] }
    enSegundos: { nodes: Novedad[] }
  }>(
    `
    query ($cuantas: Int!, $limite: LongString!) {
      enMilisegundos: chapters(
        filter: { inLibrary: { equalTo: true }, uploadDate: { greaterThan: $limite } }
        order: [{ by: UPLOAD_DATE, byType: DESC }]
        first: $cuantas
      ) { ${CAMPOS_NOVEDAD} }
      enSegundos: chapters(
        filter: { inLibrary: { equalTo: true }, uploadDate: { lessThan: $limite } }
        order: [{ by: UPLOAD_DATE, byType: DESC }]
        first: $cuantas
      ) { ${CAMPOS_NOVEDAD} }
    }
  `,
    { cuantas, limite: LIMITE_UNIDADES },
  )

  return [...datos.enMilisegundos.nodes, ...datos.enSegundos.nodes]
    .sort((a, b) => aMilisegundos(b.uploadDate) - aMilisegundos(a.uploadDate))
    .slice(0, cuantas)
}
